<template>
    <div>
        <!-- delete dialog -->
        <delete-dialog ref="DeleteDialog" @on-delete="fetchAll">
            <p class="mb-0 text-body-1">
                {{ $t('are-you-sure-you-want-to') }} <span class="red--text">{{ $t('delete') }}</span> {{$t('Class.the-classification') + $t('?') }}
            </p>
        </delete-dialog>
        <!-- dialog -->
        <v-dialog v-model="dialog" persistent width="400px" @keydown.esc="dialog = false" >
			<v-card>
				<v-form ref="form" @submit.prevent="save">
					<div class="d-flex justify-end py-1 background-title-card">
                        <v-card-title
                            class="text-uppercase text-subtitle-1 d-flex justify-center w-100 px-0 py-0 white--text"
                        >
                            {{ 
                                classificationId ? $t('Class.edit-classification') : $t('Class.add-new-classification') 
                            }}
                        </v-card-title>

                        <v-btn icon @click="dialog = false">
                            <v-icon color="white"> mdi-close-circle-outline </v-icon>
                        </v-btn>
                    </div>

					<v-card-text class="pb-0">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="classificationName"
                                    :label="$t('Class.classification-name')"
                                    dense
                                    outlined
                                    hide-details
                                    :rules="rules.required"
                                />
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="order"
                                    :label="$t('Class.priority')"
                                    dense
                                    outlined
                                    hide-details
                                    :rules="rules.requiredAsNumber"
                                />
                            </v-col>
                        </v-row>
					</v-card-text>
					<v-card-actions class="pt-0">
						<v-spacer />
						<v-btn type="submit" text color="success" :loading="loading"> {{ $t('save') }} </v-btn>
						<v-btn text :disabled="loading" @click="dialog = false"> {{ $t('cancel') }} </v-btn>
					</v-card-actions>
                    
				</v-form>
			</v-card>
        </v-dialog>
        <!-- headers -->
        <v-row>
            <v-col class="d-flex justify-start ">
				<!-- add new classification -->
    			<v-btn
					@click="reset()"
					small
					class="mx-2"
					color="light-blue white--text"
                    :disabled="loading"
				>                    
					<v-icon class="me-2">
                        mdi-plus-circle-outline
                    </v-icon>
                    {{$t('Class.add-new-classification')}}
                </v-btn>

                <!-- BACK -->
                <v-btn
                    color="grey darken-2 white--text"
                    small
                    :to="{
                        name: 'customers'
                    }"
                    :disabled="loading"
                >
                    <v-icon small class="me-1"> mdi-arrow-left-circle </v-icon>
                    {{$t('back')}}
                </v-btn>
            </v-col>
        </v-row>
        <!-- table -->
        <bee-handy-table
            :items="!loading ? classification : []"
            :headers="headers"
            dense
            zebra
            :loading="loading"
            fixed-header
            :height="$vuetify.breakpoint.height - 120"
            pagination-on-scroll
            class="mt-2"
        >
            <template v-slot:item.actions="{ item }">
                <v-btn
                	text
                	color="edit-color"
                	x-small
                	class="px-1"
					@click="classificationId = item.id; dialog = true"
                >
                    <v-icon> mdi-pencil </v-icon>
                </v-btn>

                <v-btn
                	text
                	color="alert-color"
                	x-small
                	class="px-1"
                    @click="$refs.DeleteDialog.activeDeleteDialog(item.id, 'workload')"
                >
                    <v-icon> mdi-delete </v-icon>
                </v-btn>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { roleName } from '@/helpers/enums'
import DeleteDialog from '@/components/DeleteDialog'
import rules from '@/validation rules'
export default {
    name: 'classification-management',

    components: {
        DeleteDialog
    },
    
    data () {
        return {
            dialog: false,

            loading: false,
			classificationId: null,
            roleName,
            classificationName: null,
            rules,
            order: null
        }
    },

	watch: {
		dialog (val) {
            if (val) {
                this.classificationName = this.getClassificationById(this.classificationId)?.name
                this.order = this.getClassificationById(this.classificationId)?.order
            } else {
                this.$refs.form.resetValidation()
            }
		}
	},
    
    computed: {
        ...mapState({
            classification: state => state.workload.classification,
            permissionsUser: state => state.auth.youPermissions,
			isSuperAdmin: state => state.auth.isSuperAdmin
        }),

        ...mapGetters({
            getClassificationById: 'workload/getClassificationById'
        }),

        headers () {
            const headers = [
                {
                    text: this.$t('headers.classification'), 
                    align: 'start', 
                    sortable: true, 
                    value: 'name', 
                    name: 'name',
                    class:'text-body-2 font-weight-bold',
                },
                {
                    text: this.$t('Class.priority'), 
                    align: 'start', 
                    sortable: true, 
                    value: 'order', 
                    name: 'priority',
                    class:'text-body-2 font-weight-bold',
                    type: 'number'
                }
            ];
            if (this.isSuperAdmin || this.permissionsUser.Entities === roleName.action) {
                headers.push({
                    text: '', 
                    align: 'start', 
                    value: 'actions', 
                    name: 'actions',
                    class:'text-body-2 font-weight-bold',
                })
            }
            return headers
        } 
    },

	methods: {
        fetchAll () {
            this.loading = true
            this.$store.dispatch('workload/fetchAll').finally(() => {
                this.loading = false
            })
        },
        save () {
            if (this.$refs.form.validate()) {
                this.loading = true
                const actionsName = this.classificationId ? 'update' : 'create'
                this.$store.dispatch(`workload/${actionsName}`, { 
                    name: this.classificationName, 
                    id: this.classificationId,
                    order: this.order
                }).then(() => {
                    this.fetchAll();
                    this.dialog = false
                }).catch(() => {
                    this.loading = false
                })
            }
        },
		reset() {
			this.classificationName = null;
			this.classificationId = null;
			this.dialog = true
		}
	},

    created () {
        if (!this.classification.length) {
            this.fetchAll()
        }
    }
}
</script>
